import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { gameAPI } from '../../services/gameAPI';
import { useGameStore } from '../../store/gameStore';
import {
  Loader2,
  BookOpen,
  Target,
  Users,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Home,
  RotateCcw
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  formatSignedValue,
  formatPenalty,
  getSignedColorClass,
  getEvidenceRelevanceRow,
  getEvidenceVerdict,
} from '../../utils/scoreFormatting';
import { approachFitFromPercent } from '../../utils/effectiveness';
import { isFeaturedSessionId } from '../../utils/featuredSession';
import { restartFeaturedCase } from '../../utils/restartFeaturedCase';

/**
 * @param {object} props
 * @param {Function} [props.onAfterRestart] - called instead of `onClose` once a
 *   featured Play Again lands, so a host that owns more than this modal (the
 *   accusation result view) can close its own layer too.
 */
export const CaseSummaryModal = ({ open, onClose, sessionId, onAfterRestart }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [restarting, setRestarting] = useState(false);
  const selectedTheme = useGameStore((state) => state.selectedTheme);
  const resetGame = useGameStore((state) => state.resetGame);
  const navigate = useNavigate();

  const themeAccent = {
    noir: '#DC143C',
    '90s_nyc': '#FF6B35',
    miami_vice: '#FF1493',
  }[selectedTheme || 'noir'];

  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Main game mode: Call backend API
      const data = await gameAPI.getCaseSummary(sessionId);
      setSummary(data);
    } catch (err) {
      console.error('Failed to fetch summary:', err);
      setError('Failed to load case summary');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (open && sessionId) {
      fetchSummary();
    }
  }, [open, sessionId, fetchSummary]);

  const handleNewGame = () => {
    resetGame();
    onClose();
    navigate('/');
  };

  // Only a featured case can be replayed: pro-gen has no restart endpoint.
  const isFeatured = isFeaturedSessionId(sessionId);
  const isLost = summary?.accusationResult !== 'correct' || !summary?.killer;

  const handlePlayAgain = async () => {
    // Double-click guard: the restart is a committing POST.
    if (restarting) return;
    setRestarting(true);
    const restarted = await restartFeaturedCase(sessionId);
    setRestarting(false);
    if (!restarted) return; // `restartFeaturedCase` already raised the toast.
    if (onAfterRestart) {
      onAfterRestart();
      return;
    }
    onClose();
  };

  // Same two derivations the accusation result view makes, from the same helpers,
  // so the two breakdowns cannot drift apart on this term. Both are null when the
  // section is absent (a score persisted before the term existed) or when the
  // accusation has not resolved — never inferred from zeros.
  const evidenceRelevance = summary?.score?.breakdown?.evidence_relevance;
  const relevanceRow = getEvidenceRelevanceRow(evidenceRelevance);
  const evidenceVerdict = getEvidenceVerdict({
    relevance: evidenceRelevance,
    isCorrect: summary?.accusationResult === 'correct',
  });

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl lg:max-w-5xl bg-slate-900 border-white/20">
          <div className="flex flex-col items-center justify-center h-64 gap-4">
            <Loader2 className="w-8 h-8 animate-spin text-white" />
            <p className="text-gray-400">Generating case summary...</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (error || !summary) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-md bg-slate-900 border-white/20">
          <div className="text-center py-8">
            <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
            <p className="text-white font-semibold mb-2">Unable to Load Summary</p>
            <p className="text-gray-400 text-sm">{error || 'Something went wrong'}</p>
            <Button onClick={onClose} className="mt-4" variant="outline">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-w-4xl lg:max-w-5xl max-h-[90vh] bg-slate-900 border-2"
        style={{ borderColor: themeAccent }}
      >
        <DialogHeader>
          <DialogTitle className="text-3xl font-bold text-white text-center">
            Case Summary
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="truth" className="flex-1">
          <TabsList className={`grid ${isLost ? 'grid-cols-2' : 'grid-cols-4'} bg-slate-800`}>
            <TabsTrigger value="truth" className="flex items-center gap-1">
              <BookOpen className="w-4 h-4" />
              <span className="hidden sm:inline">{isLost ? 'Case Unsolved' : 'The Truth'}</span>
            </TabsTrigger>
            <TabsTrigger value="score" className="flex items-center gap-1">
              <Target className="w-4 h-4" />
              <span className="hidden sm:inline">Score</span>
            </TabsTrigger>
            {!isLost && (
              <TabsTrigger value="missed" className="flex items-center gap-1">
                <AlertTriangle className="w-4 h-4" />
                <span className="hidden sm:inline">Missed</span>
              </TabsTrigger>
            )}
            {!isLost && (
              <TabsTrigger value="fates" className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                <span className="hidden sm:inline">Fates</span>
              </TabsTrigger>
            )}
          </TabsList>

          {/* The Truth Tab */}
          <TabsContent value="truth" className="mt-4">
            <ScrollArea className="h-[50vh] pr-4">
              <div className="space-y-4">
                {isLost ? (
                  <div className="bg-slate-800 rounded-lg p-4">
                    <h3 className="text-xl font-bold text-white mb-2">Case Unsolved</h3>
                    <p className="text-gray-300">
                      {isFeatured
                        ? 'This case was closed without an arrest. Play it again to find the killer.'
                        : 'This case was closed without an arrest.'}
                    </p>
                  </div>
                ) : (
                  <>
                {/* Killer Reveal */}
                <div
                  className="bg-slate-800 rounded-lg p-4 border-l-4"
                  style={{ borderColor: themeAccent }}
                >
                  <h3 className="text-xl font-bold text-white mb-2">
                    The Killer: {summary.killer.name}
                  </h3>
                  <p className="text-gray-400">
                    {summary.killer.occupation}
                  </p>
                  <p className="text-gray-300 mt-2">
                    <strong>Motive:</strong> {summary.killer.motive}
                  </p>
                </div>

                {/* Narrative */}
                <div className="prose prose-invert max-w-none">
                  <p className="text-gray-200 leading-relaxed whitespace-pre-line">
                    {summary.narrative?.theTruth}
                  </p>
                </div>
                  </>
                )}
              </div>
            </ScrollArea>
          </TabsContent>

          {/* Score Tab */}
          <TabsContent value="score" className="mt-4">
            <ScrollArea className="h-[50vh] pr-4">
              {summary.score && (
                <div className="space-y-4">
                  {/* Same verdict the result view shows: whether the evidence
                      actually established the accusation. */}
                  {evidenceVerdict && (
                    <div
                      className={`rounded-lg p-4 border ${
                        evidenceVerdict.tone === 'affirm'
                          ? 'bg-emerald-500/10 border-emerald-500/30'
                          : 'bg-amber-500/10 border-amber-500/30'
                      }`}
                      data-testid="summary-evidence-verdict"
                    >
                      <p
                        className={`text-sm leading-relaxed ${
                          evidenceVerdict.tone === 'affirm' ? 'text-emerald-100' : 'text-amber-100'
                        }`}
                      >
                        {evidenceVerdict.text}
                      </p>
                    </div>
                  )}

                  {/* Main Score Display */}
                  <div className="bg-black/60 rounded-lg p-5 border border-white/10">
                    <div className="text-center mb-4">
                      <div className="text-4xl mb-1">{summary.score.grade?.icon}</div>
                      <h3 className="text-xl font-bold" style={{ color: summary.score.grade?.color }}>
                        {summary.score.grade?.name}
                      </h3>
                      <div className="text-3xl font-bold text-white mt-2">
                        {summary.score.score} / {summary.score.maxPossible}
                      </div>
                    </div>

                    {/* Score Bar */}
                    <div className="w-full bg-gray-700 rounded-full h-3 mb-4">
                      <div
                        className="h-3 rounded-full transition-all duration-1000"
                        style={{
                          backgroundColor: summary.score.grade?.color,
                          width: `${(summary.score.score / summary.score.maxPossible) * 100}%`
                        }}
                      />
                    </div>

                    <div className="space-y-2 text-sm">
                      {/* `SCORE_CONFIG["base_score"]` is 0, so this row is a
                          permanent "+0". Rendered conditionally rather than
                          deleted so it reappears on its own if the constant is
                          ever re-tuned above zero. */}
                      {summary.score.breakdown?.base > 0 && (
                        <div className="flex justify-between text-gray-400">
                          <span>Base Score</span>
                          <span className="text-white">+{summary.score.breakdown?.base}</span>
                        </div>
                      )}

                      <div className="flex justify-between text-gray-400">
                        <span>Clues Found ({summary.score.breakdown?.clues?.found}/{summary.score.breakdown?.clues?.total})</span>
                        <span className="text-green-400">+{summary.score.breakdown?.clues?.bonus}</span>
                      </div>

                      {/* Gated on the section existing, not on its numbers: a
                          score persisted before this term existed carries none. */}
                      {summary.score.breakdown?.documents && (
                        <div className="flex justify-between text-gray-400">
                          <span>Documents Found ({summary.score.breakdown?.documents?.found}/{summary.score.breakdown?.documents?.total})</span>
                          <span className="text-green-400">+{summary.score.breakdown?.documents?.bonus}</span>
                        </div>
                      )}

                      {relevanceRow && (
                        <div className="flex justify-between text-gray-400" data-testid="summary-relevance-row">
                          <span>
                            {relevanceRow.label}
                            {relevanceRow.note && (
                              <span className="block text-xs text-gray-500">{relevanceRow.note}</span>
                            )}
                          </span>
                          <span className={getSignedColorClass(relevanceRow.bonus)}>
                            {formatSignedValue(relevanceRow.bonus)}
                          </span>
                        </div>
                      )}

                      <div className="flex justify-between text-gray-400">
                        <span>Interviews ({summary.score.breakdown?.interviews?.conducted})</span>
                        <span className="text-green-400">+{summary.score.breakdown?.interviews?.bonus}</span>
                      </div>

                      {summary.score.breakdown?.examine_skill?.examinations_count > 0 && (
                        <div className="flex justify-between text-gray-400">
                          <span>Examine Skill ({summary.score.breakdown?.examine_skill?.average_effectiveness}%)</span>
                          <span className={getSignedColorClass(summary.score.breakdown?.examine_skill?.total)}>
                            {formatSignedValue(summary.score.breakdown?.examine_skill?.total)}
                          </span>
                        </div>
                      )}

                      {/* Present in the accusation result view since it shipped;
                          absent here until now, which is why this breakdown could
                          not sum. */}
                      {summary.score.breakdown?.interrogation_skill?.average_effectiveness > 0 && (
                        <div className="flex justify-between text-gray-400">
                          <span>
                            {/* The row is gated on `average_effectiveness > 0`,
                                so the value is always a number here. */}
                            Interrogation Skill ({
                              approachFitFromPercent(
                                summary.score.breakdown?.interrogation_skill?.average_effectiveness
                              )?.label
                            })
                          </span>
                          <span className={getSignedColorClass(summary.score.breakdown?.interrogation_skill?.adjustment)}>
                            {formatSignedValue(summary.score.breakdown?.interrogation_skill?.adjustment)}
                          </span>
                        </div>
                      )}

                      {summary.score.breakdown?.consultations?.used > 0 && (
                        <div className="flex justify-between text-gray-400">
                          <span>Consultations Used ({summary.score.breakdown?.consultations?.used})</span>
                          {/* `total_penalty` is already negative — do NOT negate it again. */}
                          <span className="text-red-400">
                            {formatSignedValue(summary.score.breakdown?.consultations?.total_penalty)}
                          </span>
                        </div>
                      )}

                      {/* Cipher Puzzle */}
                      {summary.score.breakdown?.cipher && !summary.score.breakdown?.cipher?.not_available && (
                        <div className="flex justify-between text-gray-400">
                          <span>
                            Cipher Puzzle {summary.score.breakdown?.cipher?.solved ? '(Solved)' : summary.score.breakdown?.cipher?.unlocked ? '(Unsolved)' : '(Not Found)'}
                          </span>
                          <span className={getSignedColorClass(summary.score.breakdown?.cipher?.net)}>
                            {formatSignedValue(summary.score.breakdown?.cipher?.net)}
                          </span>
                        </div>
                      )}

                      <div className="flex justify-between text-gray-400">
                        <span>Turns ({summary.score.breakdown?.turns?.count ?? 0})</span>
                        <span className={getSignedColorClass(summary.score.breakdown?.turns?.bonus)}>
                          {formatSignedValue(summary.score.breakdown?.turns?.bonus)}
                        </span>
                      </div>

                      <div className="flex justify-between text-gray-400 border-t border-white/10 pt-2 mt-2">
                        <span>Accusation</span>
                        <span className={summary.accusationResult === 'correct' ? 'text-green-400' : 'text-red-400'}>
                          {summary.accusationResult === 'correct'
                            ? `+${summary.score.breakdown?.accusation?.bonus ?? 0}`
                            : formatPenalty(summary.score.breakdown?.accusation?.penalty)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-800 rounded-lg p-4">
                      <h4 className="text-sm text-gray-400 mb-1">Turns Used</h4>
                      <p className="text-2xl font-bold text-white">
                        {summary.turnsUsed}
                      </p>
                    </div>
                    <div className="bg-slate-800 rounded-lg p-4">
                      <h4 className="text-sm text-gray-400 mb-1">Discovery Rate</h4>
                      <p className="text-2xl font-bold text-white">
                        {summary.discoveryStats.clues.percentage}%
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </ScrollArea>
          </TabsContent>

          {/* Missed Clues Tab */}
          {!isLost && <TabsContent value="missed" className="mt-4">
            <ScrollArea className="h-[50vh] pr-4">
              {(summary?.missedClues?.length ?? 0) === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                  <p className="text-green-400 font-semibold">
                    Perfect Investigation!
                  </p>
                  <p className="text-gray-400">
                    You found every clue.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-gray-400 mb-4">
                    You missed {summary?.missedClues?.length ?? 0} clue(s):
                  </p>
                  {(summary?.missedClues ?? []).map((clue, index) => (
                    <div
                      key={index}
                      className="bg-slate-800 rounded-lg p-4 border-l-4 border-yellow-500"
                    >
                      <h4 className="text-white font-semibold flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-yellow-500" />
                        {clue.name}
                      </h4>
                      <p className="text-gray-400 text-sm mt-1">
                        {clue.description}
                      </p>
                      {clue.location && (
                        <p className="text-gray-500 text-xs mt-2">
                          Location: {clue.location}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>}

          {/* Character Fates Tab */}
          {!isLost && <TabsContent value="fates" className="mt-4">
            <ScrollArea className="h-[50vh] pr-4">
              {summary.narrative?.characterFates?.length > 0 ? (
                <div className="space-y-3">
                  {summary.narrative.characterFates.map((char, index) => (
                    <div
                      key={index}
                      className="bg-slate-800 rounded-lg p-4"
                    >
                      <h4 className="text-white font-semibold mb-1">
                        {char.name}
                      </h4>
                      <p className="text-gray-400 text-sm">
                        {char.fate}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  Character fates not available.
                </div>
              )}
            </ScrollArea>
          </TabsContent>}
        </Tabs>

        {/* Actions */}
        <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t border-white/10">
          <Button
            variant="outline"
            className="flex-1 border-white/20 text-white hover:bg-white/10"
            onClick={handleNewGame}
          >
            <Home className="w-4 h-4 mr-2" />
            New Game
          </Button>
          {isFeatured && (
            <Button
              variant="outline"
              className="flex-1 border-white/20 text-white hover:bg-white/10"
              onClick={handlePlayAgain}
              disabled={restarting}
              data-testid="summary-play-again"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              <span aria-live="polite">{restarting ? 'Restarting...' : 'Play Again'}</span>
            </Button>
          )}
          <Button
            onClick={onClose}
            className="flex-1 font-semibold"
            style={{ backgroundColor: themeAccent }}
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
